import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { OAuthService, NullValidationHandler, AuthConfig } from 'angular-oauth2-oidc';
import { HttpClient } from '@angular/common/http';
import { Spinkit } from 'ng-http-loader';
import { Service } from './service';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';
import { AppConfigService } from './services/app-config.service';
import { AppVersion } from './common/models/appversion';
import { LoggingService } from './services/logging.service';
import { PermissionService } from './services/permission.service';
import { MasterDataService } from './services/master-data.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  @ViewChild('continueSessionButton') continueSessionButton!: ElementRef<HTMLButtonElement>;

  title = 'FCWeb';
  isSortMenu = false;
  isAutenticate: boolean = false;
  spinnerStyle = Spinkit;
  loginName: string = ""
  loginEmail: string = "";
  userDetails: any = {};
  claimObj: any = [];
  isAdmin: boolean = false;
  timestamp: number = 0;
  currentTime: number = 0;
  closeResult: string = '';
  authConfig: AuthConfig = new AuthConfig();
  discoveryDocumentUrl: string = '';
  appVersion: AppVersion = new AppVersion();
  returnUrl: string = "";
  enableEDIOrder: boolean = false;
  showSessionTimeoutWarning: boolean = false;
  sessionTimeRemainingSeconds: number = 0;
  private sessionTimeoutMs: number = 0;
  private sessionWarningMs: number = 0;
  private sessionExpiresAt: number = 0;
  private sessionTimeoutHandle: number | null = null;
  private sessionWarningHandle: number | null = null;
  private sessionCountdownHandle: number | null = null;
  private sessionLogHandle: number | null = null;
  private lastActivityResetAt: number = 0;

  constructor(private oauthService: OAuthService, private apiService: Service, private router: Router,
    private modalService: NgbModal, private httpClient: HttpClient, private appConfigService: AppConfigService,
    private loggingService: LoggingService, private route: ActivatedRoute,private permissionService: PermissionService,
    private masterDataService: MasterDataService) {
    const url = window.location.href.toString();

    if (url.indexOf(appConfigService.passwordResetErrorCode) > 0) {
      window.location.href = appConfigService.passwordResetUrl;
    }
    else {
      this.authConfig.redirectUri = appConfigService.redirectUri;
      this.authConfig.responseType = appConfigService.responseType;
      this.authConfig.issuer = appConfigService.issuer;
      this.authConfig.strictDiscoveryDocumentValidation = appConfigService.strictDiscoveryDocumentValidation;
      this.authConfig.tokenEndpoint = appConfigService.tokenEndpoint;
      this.authConfig.loginUrl = appConfigService.loginUrl;
      this.authConfig.clientId = appConfigService.clientId;
      this.authConfig.scope = appConfigService.scope;
      this.authConfig.skipIssuerCheck = appConfigService.skipIssuerCheck;
      this.authConfig.clearHashAfterLogin = appConfigService.clearHashAfterLogin;
      this.authConfig.oidc = appConfigService.oidc;
      this.authConfig.postLogoutRedirectUri = appConfigService.redirectUri;
      this.discoveryDocumentUrl = appConfigService.DiscoveryDocumentUrl;
      this.applySessionTimeoutSettings(
        appConfigService.appConfig.SessionTimeoutInMinutes,
        appConfigService.appConfig.SessionWarningInMinutes
      );

      this.configure();

      //Access token refresh in background before token expiry time
      this.oauthService.setupAutomaticSilentRefresh();            
    }      
  }
  
  ngOnDestroy(): void {
    this.clearSessionTimers();
    sessionStorage.clear();
  }

  @HostListener('document:click')
  @HostListener('document:keydown')
  @HostListener('document:mousemove')
  @HostListener('document:scroll')
  @HostListener('document:touchstart')
  onUserActivity(): void {
    if (!this.isAutenticate || this.showSessionTimeoutWarning) {
      return;
    }

    const now = Date.now();
    if (now - this.lastActivityResetAt < 1000) {
      return;
    }

    this.lastActivityResetAt = now;
    this.startSessionTimeoutMonitoring();
  }

  @HostListener('window:focus')
  @HostListener('document:visibilitychange')
  onSessionResume(): void {
    this.evaluateSessionState();
    window.setTimeout(() => {
      this.evaluateSessionState();
    }, 0);
  }

  @HostListener('document:keydown', ['$event'])
  lockSessionWarningKeyboard(event: KeyboardEvent): void {
    if (!this.showSessionTimeoutWarning) {
      return;
    }

    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.focusContinueButton();
    }
  }

  login() {
    this.oauthService.initImplicitFlow();    
  }

  logout(preserveCurrentRoute: boolean = false) {
    this.clearSessionTimers();
    this.showSessionTimeoutWarning = false;
    this.isAutenticate = false;
    const logoutUrl = this.appConfigService.buildLogoutUrl(preserveCurrentRoute ? this.getCurrentRouteForReturn() : undefined);
    this.modalService.dismissAll();
    // Clear ALL local state before redirecting — code after logOut() never runs
    // because B2C immediately redirects the browser.
    sessionStorage.clear();
    localStorage.clear();
    this.permissionService.clearPermissions();
    this.masterDataService.clearMasterDataCache();
    this.oauthService.stopAutomaticRefresh();
    // Redirect to B2C end_session_endpoint so the SSO cookie is destroyed.
    // This prevents silent re-authentication when the user clicks Login again.
    this.oauthService.logOut(false);
    window.location.href = logoutUrl;
  }

  get allClaims() {
    let claims = this.oauthService.getIdentityClaims();
    if (claims !== null && claims !== undefined && this.isAutenticate === false) {      
      this.isAutenticate = true;
      this.startSessionTimeoutMonitoring();
      this.getLoggedInUserDetails(claims["oid"]);
      this.claimObj = claims;      
    }
    return claims ? claims : null;
  }

  continueSession() {
    if (!this.isAutenticate) {
      return;
    }

    this.closeSessionTimeoutModal();
    this.startSessionTimeoutMonitoring();
  }

  get sessionCountdownDisplay(): string {
    const minutes = Math.floor(this.sessionTimeRemainingSeconds / 60);
    const seconds = this.sessionTimeRemainingSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  configure() {
    this.oauthService.configure(this.authConfig);
    this.oauthService.tokenValidationHandler = new NullValidationHandler();
    if (this.oauthService.getAccessTokenExpiration()) {
      this.timestamp = this.oauthService.getAccessTokenExpiration();
      this.currentTime = Date.now();
      if (this.currentTime >= this.timestamp) {
        this.oauthService.logOut();
        this.isAutenticate = false;
      }
    }

    if (this.oauthService.getAccessToken() === null) {
      this.oauthService.loadDiscoveryDocument(this.discoveryDocumentUrl).then
        (
          (success) => {
            this.returnUrl = this.route.snapshot.queryParams['returnUrl'];
            if(this.returnUrl != undefined && this.returnUrl != null) {
               sessionStorage.setItem('returnUrl', this.returnUrl);
            }
            this.oauthService.tryLoginImplicitFlow();
          },
          (onrejected) => {
          });
    }
    else {
      this.oauthService.tryLoginImplicitFlow();
    }
  }

  //Get login user details from DB
  getLoggedInUserDetails(aadObjId: string) {
    if (this.userDetails !== null && this.userDetails !== undefined) {
      this.apiService
        .getUserDetails(aadObjId)
          .subscribe({
            next: (data: any) => {
              if (data != null) {
                this.userDetails = data;
                this.loginName = this.userDetails["displayName"];
                this.loginEmail = this.userDetails["emailId"];
                this.isAdmin = this.apiService.isAdmin();
                this.enableEDIOrder = this.userDetails["enableEDIOrder"];
                sessionStorage.setItem('role', this.userDetails["role"]);
                this.loadVersionInfo();
                this.permissionService.loadUserPermissions();
                this.restoreReturnUrl();
              }
            },
            error: () => {
            }
          });
    }
  }

  //Open the Verison Info popup
  open(content: any) {
    this.loadVersionInfo();
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title', backdrop: 'static', keyboard: false }).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
    });
  }

  //Closing the modal popup
  modalClose(content: any) {
    this.modalService.dismissAll();
  }

  getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  openUserManual() {
    this.httpClient.get("assets/usermanual/UserManual.pdf", { responseType: 'blob' }).subscribe(data => {
      if (data != null) {
        const fileURL = URL.createObjectURL(data);
        window.open(fileURL, '_blank');
      }
    });
  }

  private startSessionTimeoutMonitoring() {
    this.clearSessionTimers();
    this.closeSessionTimeoutModal();
    this.showSessionTimeoutWarning = false;
    this.sessionTimeRemainingSeconds = Math.floor(this.sessionWarningMs / 1000);

    if (!this.isAutenticate || this.sessionTimeoutMs <= 0) {
      return;
    }

    this.sessionExpiresAt = Date.now() + this.sessionTimeoutMs;

    const warningDelay = Math.max(this.sessionTimeoutMs - this.sessionWarningMs, 0);
    this.sessionWarningHandle = window.setTimeout(() => {
      this.openSessionTimeoutWarning();
    }, warningDelay);

    this.sessionTimeoutHandle = window.setTimeout(() => {
      this.logout(true);
    }, this.sessionTimeoutMs);

    console.info('[FC session-timeout] idle timer restarted', {
      timeoutMinutes: this.sessionTimeoutMs / 60000,
      warningMinutes: this.sessionWarningMs / 60000,
      expiresAt: new Date(this.sessionExpiresAt).toISOString()
    });

    this.startSessionIdleLogging();
  }

  private openSessionTimeoutWarning() {
    if (!this.isAutenticate) {
      return;
    }

    this.showSessionTimeoutWarning = true;
    this.updateSessionTimeRemaining();

    if (this.sessionCountdownHandle !== null) {
      return;
    }

    this.focusContinueButton();
    this.sessionCountdownHandle = window.setInterval(() => {
      this.updateSessionTimeRemaining();
    }, 1000);
  }

  private clearSessionTimers() {
    this.sessionExpiresAt = 0;

    if (this.sessionTimeoutHandle !== null) {
      window.clearTimeout(this.sessionTimeoutHandle);
      this.sessionTimeoutHandle = null;
    }

    if (this.sessionWarningHandle !== null) {
      window.clearTimeout(this.sessionWarningHandle);
      this.sessionWarningHandle = null;
    }

    if (this.sessionCountdownHandle !== null) {
      window.clearInterval(this.sessionCountdownHandle);
      this.sessionCountdownHandle = null;
    }

    if (this.sessionLogHandle !== null) {
      window.clearInterval(this.sessionLogHandle);
      this.sessionLogHandle = null;
    }
  }

  private closeSessionTimeoutModal() {
    this.showSessionTimeoutWarning = false;
  }

  private focusContinueButton() {
    window.setTimeout(() => {
      if (this.continueSessionButton && this.continueSessionButton.nativeElement) {
        this.continueSessionButton.nativeElement.focus();
      }
    });
  }

  private restoreReturnUrl() {
    const storedReturnUrl = sessionStorage.getItem('returnUrl');
    if (!storedReturnUrl || !storedReturnUrl.startsWith('/')) {
      sessionStorage.removeItem('returnUrl');
      return;
    }

    sessionStorage.removeItem('returnUrl');
    if (this.router.url !== storedReturnUrl) {
      this.router.navigateByUrl(storedReturnUrl);
    }
  }

  private getCurrentRouteForReturn(): string {
    const currentRoute = this.router.url || '/home';
    if (currentRoute === '/login') {
      return '/home';
    }

    return currentRoute.startsWith('/') ? currentRoute : `/${currentRoute}`;
  }

  private loadVersionInfo() {
    this.masterDataService
      .getVersionInfo()
      .subscribe({
        next: (data: any) => {
          if (data != null) {
            this.appVersion.AppName = data.appName;
            this.appVersion.AppVersion = data.appVersion;
            this.appVersion.CIMSVersion = data.cimsVersion;
            this.appVersion.G3MSVersion = data.g3MSVersion;
            this.appVersion.BuildDate = data.buildDate;
            this.applySessionTimeoutSettings(data.sessionTimeOutMin, data.sessionWarningTime);

            if (this.isAutenticate) {
              this.startSessionTimeoutMonitoring();
            }
          }
        },
        error: () => {
        }
      });
  }

  private applySessionTimeoutSettings(sessionTimeoutInMinutes?: number, sessionWarningInMinutes?: number) {
    const timeoutInMinutes = this.getValidSessionTimeoutValue(sessionTimeoutInMinutes, 50);
    const warningInMinutes = this.getValidSessionTimeoutValue(sessionWarningInMinutes, 2);

    this.sessionTimeoutMs = timeoutInMinutes * 60 * 1000;
    this.sessionWarningMs = Math.min(warningInMinutes * 60 * 1000, this.sessionTimeoutMs);
    this.sessionTimeRemainingSeconds = Math.floor(this.sessionWarningMs / 1000);
  }

  private getValidSessionTimeoutValue(value: number | undefined, fallbackValue: number): number {
    return typeof value === 'number' && value > 0 ? value : fallbackValue;
  }

  private updateSessionTimeRemaining() {
    if (this.sessionExpiresAt > 0) {
      this.sessionTimeRemainingSeconds = Math.max(Math.ceil((this.sessionExpiresAt - Date.now()) / 1000), 0);
      return;
    }

    this.sessionTimeRemainingSeconds = Math.floor(this.sessionWarningMs / 1000);
  }

  private startSessionIdleLogging() {
    this.sessionLogHandle = window.setInterval(() => {
      this.evaluateSessionState();
      this.logRemainingIdleTime();
    }, 5 * 60 * 1000);
  }

  private logRemainingIdleTime() {
    if (!this.isAutenticate || this.sessionExpiresAt <= 0) {
      return;
    }

    const remainingSeconds = Math.max(Math.ceil((this.sessionExpiresAt - Date.now()) / 1000), 0);
    console.info('[FC session-timeout] remaining idle time', {
      remainingSeconds,
      remainingMinutes: Number((remainingSeconds / 60).toFixed(2)),
      warningStartsAt: new Date(this.sessionExpiresAt - this.sessionWarningMs).toISOString(),
      expiresAt: new Date(this.sessionExpiresAt).toISOString()
    });
  }

  private evaluateSessionState() {
    if (!this.isAutenticate) {
      return;
    }

    const accessTokenExpiration = this.oauthService.getAccessTokenExpiration();
    const now = Date.now();

    if ((this.sessionExpiresAt > 0 && now >= this.sessionExpiresAt)
      || (accessTokenExpiration > 0 && now >= accessTokenExpiration)) {
      this.logout(true);
      return;
    }

    if (this.sessionWarningMs > 0 && this.sessionExpiresAt > 0) {
      const warningWindowStart = this.sessionExpiresAt - this.sessionWarningMs;
      if (now >= warningWindowStart) {
        this.openSessionTimeoutWarning();
      }
    }
  }
}
